import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(REDUCE_QUERY).matches
  );

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCE_QUERY);
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return reduced;
}

// True once the element has entered the viewport. With `once: false` it follows the element in and out,
// which is how looping animations pause while they are off screen.
export function useInView(ref, { once = true, rootMargin = '0px 0px -10% 0px', threshold = 0.12 } = {}) {
  const [inView, setInView] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin, threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, once, rootMargin, threshold]);

  return inView;
}

// Scroll-triggered entrance. Variants are defined in index.css (.reveal-*); reduced motion shows content at rest.
export function Reveal({ as: Tag = 'div', variant = 'up', delay = 0, className = '', style, children, ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <Tag
      ref={ref}
      className={`reveal reveal-${variant} ${inView ? 'is-in' : ''} ${className}`}
      style={{ '--reveal-delay': `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// Text that settles in word by word when it scrolls into view.
export function WordsReveal({ text, offset = 0, step = 38, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.35 });
  const words = text.split(' ');
  return (
    <span ref={ref} className={className}>
      {words.map((word, i) => (
        <React.Fragment key={`${word}-${i}`}>
          <span className={`reveal-word ${inView ? 'is-in' : ''}`} style={{ transitionDelay: `${(offset + i) * step}ms` }}>
            {word}
          </span>
          {i < words.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </span>
  );
}

// Steps from 0 to `total`, one step per `interval`, while `running`. Holds its place while paused and
// starts at `total` under reduced motion. Returns [step, setStep] so callers can restart or jump.
export function useSequence(total, { interval = 400, running = true } = {}) {
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(reduced ? total : 0);

  useEffect(() => {
    if (reduced) {
      setStep(total);
      return undefined;
    }
    if (!running || step >= total) return undefined;
    const id = setTimeout(() => setStep((s) => Math.min(total, s + 1)), interval);
    return () => clearTimeout(id);
  }, [reduced, running, step, total, interval]);

  return [step, setStep];
}

// Counts from `from` to `to` once `start` turns true; jumps straight to `to` under reduced motion.
export function useCountUp(to, { from = 0, duration = 1400, start = true } = {}) {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(reduced ? to : from);

  useEffect(() => {
    if (!start) return undefined;
    if (reduced) {
      setValue(to);
      return undefined;
    }
    let frame;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 4);
      setValue(Math.round(from + (to - from) * eased));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to, from, duration, start, reduced]);

  return value;
}
